import type { AxiosError } from 'axios';

function extractApiErrorMessage(error: unknown): string | undefined {
    if (typeof error === 'string') return error;
    if (
        error !== null &&
        typeof error === 'object' &&
        'message' in error &&
        typeof (error as { message: unknown }).message === 'string'
    ) {
        return (error as { message: string }).message;
    }
    return undefined;
}

/**
 * Turn an unknown thrown value from an `apiV2.*()` call into a clean `Error`
 */
export function toApiError(err: unknown): Error {
    const axiosError = err as AxiosError<{
        success: boolean;
        error: string | { code?: string; message?: string };
    }>;
    const responseBody = axiosError.response?.data;
    const message =
        extractApiErrorMessage(responseBody?.error) ??
        extractApiErrorMessage(err) ??
        'An unexpected error occurred.';
    return new Error(message);
}

/** `responseType: 'blob'` forces error bodies into a Blob too, so re-parse it as
 *  JSON so toApiError can still read the backend's real message. */
export async function parseBlobErrorResponse(err: unknown): Promise<unknown> {
    if (typeof err !== 'object' || err === null) return err;
    const response = (err as { response?: { data?: unknown } }).response;
    if (!(response?.data instanceof Blob)) return err;
    try {
        const parsed: unknown = JSON.parse(await response.data.text());
        return { ...err, response: { ...response, data: parsed } };
    } catch {
        return err;
    }
}
